<template>
    <div>
        <h3 class="mt-3 mb-1">
            {{ $l10n.__('Recherche de documents') }}
        </h3>
        <p class="grey m-0">
            {{ $l10n.__('Complétez un ou plusieurs champs et lancez votre recherche') }}
        </p>

        <rb-filter
            :transactionId="transactionId"
            :transactionType="transactionType"
            :transactionDate="DateObjTransaction"
        />
        <preloader :display="loadingDocs" />
        <rb-document-listing v-if="!loadingDocs" class="mt-1" />

        <fixed-footer v-if="reconciled.length > 1">
            <el-row>
                <el-col :span="8">
                    {{ $l10n.__('Rapprochement multiple : ') }}
                    <span class="grey fs-4">
                        ({{ $l10n.__('%d actions', reconciled.length) }})
                    </span>
                </el-col>
                <el-col :span="6">
                    <rb-footer-amount :transactionType="transactionType" />
                </el-col>
                <el-col :span="8" :offset="2">
                    <CompassButton
                        :label="
                            $t.__('bankreconciliation.DocumentForm.see_selection_and_reconcile')
                        "
                        type="tertiary"
                        @click="dialogVisible = true"
                    />
                </el-col>
            </el-row>
        </fixed-footer>

        <fixed-footer v-else>
            <el-row class="container-big m-auto">
                <el-col :span="8" :offset="8">
                    <rb-footer-amount :transactionType="transactionType" />
                </el-col>
                <el-col :span="8" class="aright">
                    <CompassButton
                        :label="$t.__('common.actions.save_and_back_to_list')"
                        type="tertiary"
                        :loading="loading"
                        class="mr-2"
                        @click="recordReconciliations('backward')"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        type="primary"
                        :loading="loading"
                        @click="recordReconciliations('reload')"
                    />
                </el-col>
            </el-row>
        </fixed-footer>

        <CompassModal v-model:visible="dialogVisible" size="large" class="document-form">
            <template #header>
                <div class="dialog__header">
                    <div class="dialog__header--container">
                        <span class="dialog__header--title">
                            {{ $l10n.__('Rapprochement multiple') }}
                        </span>
                        <span class="gray-60">
                            ({{ $l10n.__('%d actions', reconciled.length) }})
                        </span>
                    </div>
                    <div class="dialog__header--close">
                        <CompassIconButton icon="close" @click="dialogVisible = false" />
                    </div>
                </div>
            </template>
            <template #default>
                <div class="dialog__default">
                    <el-table :data="reconciled" style="width: 100%">
                        <el-table-column
                            prop="document.thirdname"
                            :label="$t.__('common.labels.name')"
                        >
                        </el-table-column>
                        <el-table-column :label="$t.__('common.labels.document_number')">
                            <template #default="scope">
                                <a :href="scope.row.document.link" target="_blank">
                                    {{ scope.row.document.ident }}
                                </a>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t.__('common.labels.amount_including_tax')">
                            <template #default="scope">
                                {{ $filters.numberFormatCurrency(scope.row.document.totalAmount) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t.__('bankreconciliation.DocumentForm.payment')">
                            <template #default="scope">
                                {{ $filters.numberFormatCurrency(scope.row.amount) }}
                            </template>
                        </el-table-column>
                    </el-table>
                    <p class="aright">
                        {{ $l10n.__('Total rapproché : ') }}
                        <strong>{{ $filters.numberFormatCurrency(totalReconciled) }}</strong>
                    </p>
                </div>
            </template>
            <template #footer>
                <div class="dialog__footer">
                    <CompassButton
                        :label="$t.__('common.actions.save_and_back_to_list')"
                        type="tertiary"
                        :loading="loading"
                        @click="recordReconciliations('backward')"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        type="primary"
                        :loading="loading"
                        class="ml-3"
                        @click="recordReconciliations('reload')"
                    />
                </div>
            </template>
        </CompassModal>
    </div>
</template>

<script>
import DocumentListing from './DocumentListing.vue';
import ReconciliationStore from './../../store/reconciliation.js';
import Filter from './Filter.vue';
import FooterAmount from './FooterAmount.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import FixedFooter from 'vcomponents/utilities/FixedFooter.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters: reconciliationGetters, mapMutations: reconciliationMutations } =
    createNamespacedHelpers('reconciliationStore');

export default {
    components: {
        Preloader,
        'fixed-footer': FixedFooter,
        'rb-filter': Filter,
        'rb-document-listing': DocumentListing,
        'rb-footer-amount': FooterAmount,
    },
    props: {
        transactionType: {
            type: String,
            required: true,
        },
        transactionId: {
            type: Number,
            required: true,
        },
        transactionDate: {
            type: String,
            required: true,
        },
        transactionRemainingReconciledAmount: {
            type: Number,
            required: true,
        },
        transactionAmount: {
            type: Number,
            required: true,
        },
        transactionReconciledAmount: {
            type: Number,
            required: true,
        },
        currencySymbol: {
            type: String,
            required: false,
            default: '€',
        },
    },
    data() {
        return {
            dialogVisible: false,
            loading: false,
        };
    },
    computed: {
        ...reconciliationGetters(['reconciled', 'loadingDocs', 'docs']),
        totalReconciled: function () {
            const reconciledPaymentAmounts = this.reconciled.map((reconciled) => {
                if (reconciled.pay_type === 'credit') return reconciled.amount * -1;
                else return reconciled.amount;
            });
            return reconciledPaymentAmounts.reduce(
                (accumulator, currentValue) => parseFloat(accumulator) + parseFloat(currentValue),
                0,
            );
        },
        DateObjTransaction: function () {
            const cutDate = this.transactionDate.split(' ')[0].split('-');
            return new Date(cutDate[0], cutDate[1] - 1, cutDate[2]);
        },
    },
    created() {
        if (!this.$store.state.reconciliationStore) {
            this.$store.registerModule('reconciliationStore', ReconciliationStore);
        }
    },
    mounted: function () {
        this.setTransactionId(this.transactionId);
        this.setTransactionDate(this.transactionDate);
        this.setTransactionAmount(this.transactionAmount);
        this.setTransactionReconciledAmount(this.transactionReconciledAmount);
        this.setTransactionRemainingReconciledAmount(this.transactionRemainingReconciledAmount);
        this.setTransactionType(this.transactionType);
        this.setTransactionCurrencySymbol(
            this.currencySymbol || this.$store.state['corp']['numformat'],
        );
    },
    unmounted() {
        if (this.$store.state.reconciliationStore) {
            this.$store.unregisterModule('reconciliationStore');
        }
    },
    methods: {
        ...reconciliationMutations([
            'setTransactionId',
            'setTransactionDate',
            'setTransactionAmount',
            'setTransactionReconciledAmount',
            'setTransactionRemainingReconciledAmount',
            'setTransactionType',
            'setTransactionCurrencySymbol',
        ]),
        recordReconciliations: function (redirect) {
            this.loading = true;
            const option = {
                recdoc_transactionId: this.transactionId,
                recdoc_newpayment: {},
                recdoc_payments: {},
                recdoc_deadlines: {},
            };

            this.reconciled.map((reconciled) => {
                const key = reconciled.document.type + '_' + reconciled.document.id;

                if (reconciled.paymentid === undefined && reconciled.deadlines === undefined) {
                    // For new payment
                    option.recdoc_newpayment[key] = {
                        amount: reconciled.amount,
                        ref: reconciled.ref,
                        pay_type: reconciled.pay_type,
                    };
                } else {
                    // For existing payment
                    if (option.recdoc_payments[key] === undefined) {
                        option.recdoc_payments[key] = [];
                    }
                    option.recdoc_payments[key].push(reconciled.paymentid);
                }

                // For deadline
                if (
                    reconciled.deadlines !== undefined &&
                    option.recdoc_deadlines[key] === undefined
                ) {
                    option.recdoc_deadlines[key] = {
                        amount: reconciled.amount,
                        deadlineids: [],
                    };
                    reconciled.deadlines.map((deadline) => {
                        option.recdoc_deadlines[key].deadlineids.push(deadline.deadlineid);
                    });
                }
                return reconciled;
            });
            $.post(
                './?_f=bankreconciliation_reconciliation&action=reconciliateDocument',
                option,
                (data) => {
                    if (data.form.status != 'ok') {
                        this.loading = false;
                        if (data.form.msg != '') {
                            Push.error(data.form.msg);
                        } else {
                            data.form.fields.forEach((field) => {
                                const doc = field.fieldId.split('[')[1].split(']')[0].split('_');
                                const doctype = doc[0];
                                const docid = doc[1];

                                const reconciledField = this.reconciled.find((reconciled) => {
                                    return (
                                        reconciled.document.type == doctype &&
                                        reconciled.document.id == docid
                                    );
                                });

                                const msg = `${reconciledField.document.ident} : ${field.msg}`;
                                Push.error(msg);
                            });
                        }
                        return;
                    }
                    if (redirect == 'reload') {
                        location.reload();
                    } else if (redirect == 'backward') {
                        location.assign('?_f=bankreconciliation_transactions');
                    }
                },
                'json',
            );
        },
    },
};
</script>

<style lang="scss">
.document-form {
    .dialog {
        &__header {
            display: flex;
            justify-content: space-between;
            padding: 24px 32px;

            &--container {
                display: flex;
                align-items: center;
                font-family: 'Proxima Nova';
                font-style: normal;
                font-size: 20px;
                line-height: 24px;
            }

            &--title {
                font-weight: 700;
                margin-right: 5px;
            }

            &--close {
                display: flex;
                justify-content: flex-end;
                align-items: flex-start;
            }
        }

        &__default {
            padding-bottom: 24px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }
}
</style>
